import React, { useState } from 'react';
import { MenuItem, TextField, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import dayjs from 'dayjs';

import styles from './BookingPanel.module.scss';
import { ERROR_TOOLTIP_PROPS, CALENDAR_SX, TEXT_FIELD_SX } from './constant';
import { BookingPanelProps } from './types';
import { useBookingPanel } from './hooks';

import virtualIcon from '../../assets/images/virtual.png';
import presencialIcon from '../../assets/images/presencial.png';

const SHARED_MENU_PROPS = {
    PaperProps: {
        sx: {
            borderRadius: '16px',
            border: '1px solid rgba(12,36,28,.1)',
            boxShadow: '0 16px 28px rgba(19,35,29,.10)',
            backgroundColor: '#F7F5F1',
            mt: '7px',
            maxHeight: '200px',
            '& .MuiList-root': {
                padding: '6px',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
            },
            '& .MuiMenuItem-root': {
                minHeight: '42px',
                padding: '0 12px',
                borderRadius: '11px',
                fontSize: '12px',
                fontWeight: 750,
                color: '#13231d',
                fontFamily: '"Sora", sans-serif',
                transition: 'background .16s ease, color .16s ease',
                '&:hover': {
                    backgroundColor: 'rgba(130,177,148,.11)',
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: 'rgba(130,177,148,.16)',
                    color: '#345443',
                    boxShadow: 'inset 0 0 0 1px rgba(130,177,148,.40)'
                }
            }
        }
    },
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    },
} as any;

export const BookingPanel: React.FC<BookingPanelProps> = (props) => {
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
    const {
        viewsConfirmDate = false,
        modality = 'Presencial',
        loading = false,
        consultPrice,
        bookingMode = 'direct',
        messagingEnabled = true,
        isPassivePreview = false,
    } = props;

    const {
        t,
        currentLocale,
        displayDate,
        displayTime,
        timeOptions,
        timeError,
        ServerDay,
        dayTooltipOpen,
        timeTooltipOpen,
        selectDayFirstTooltipOpen,
        bookingModality,
        modalityTooltipOpen,
        setModalityTooltipOpen,
        setDayTooltipOpen,
        setTimeTooltipOpen,
        setSelectDayFirstTooltipOpen,
        setBookingModality,
        handleDateUpdate,
        handleTimeUpdate,
        handleShowBookingModal,
        shouldDisableDate,
        availabilityMap,
        validateTimeOnBlur,
        calendarHeight,
        currentMonth,
        setCurrentMonth,
        requestPeriod,
        setRequestPeriod,
        requestNote,
        setRequestNote,
        handleRequestSchedule,
        handleOpenChat,
        isSubmittingRequest,
        sessionType,
        setSessionType,
        displayPrice,
        displayDuration
    } = useBookingPanel(props);

    const renderCalendarGrid = () => {
        const firstDay = currentMonth.startOf('month');
        const startDayOfWeek = currentLocale === 'es' ? 1 : 0;
        let start = firstDay.day() - startDayOfWeek;
        if (start < 0) start += 7;

        const days = [];
        const firstCellDate = firstDay.subtract(start, 'day');
        const today = dayjs().startOf('day');

        // Find if 35 or 42 cells are needed
        const daysInMonth = currentMonth.daysInMonth();
        const totalCells = (start + daysInMonth) > 35 ? 42 : 35;

        for (let i = 0; i < totalCells; i++) {
            const d = firstCellDate.add(i, 'day');
            const inMonth = d.month() === currentMonth.month();
            const id = d.format('YYYY-MM-DD');

            let className = styles['day'];
            if (!inMonth) className += ` ${styles['day--out']}`;

            const isDisabled = shouldDisableDate(d);
            const hasSlots = availabilityMap && availabilityMap.has(id);
            const isSelected = displayDate?.format('YYYY-MM-DD') === id;

            if (bookingMode === 'direct') {
                if (!isDisabled && hasSlots) {
                    className += ` ${styles['day--available']}`;
                }
            } else {
                if (inMonth && !isDisabled) {
                    className += ` ${styles['day--requestable']}`;
                }
            }

            if (d.isSame(today, 'day')) {
                className += ` ${styles['day--today']}`;
            }
            if (isSelected) {
                className += ` ${styles['day--selected']}`;
            }

            days.push(
                <span key={id} className={styles['slot']}>
                    <button
                        type="button"
                        className={className}
                        disabled={isDisabled || !inMonth}
                        onClick={() => {
                            handleDateUpdate(d);
                            setDayTooltipOpen(false);
                        }}
                    >
                        {d.date()}
                    </button>
                </span>
            );
        }

        return days;
    };

    return (
        <div className={styles['booking-wrapper']}>
            <aside className={styles['booking']}>
                <div className={styles['booking__head']}>
                    <h3 className={`${styles['booking__title']} ${bookingMode === 'request' ? styles['booking__request-title'] : ''}`} id="requestTitle">
                        {bookingMode === 'request'
                            ? <>{t('landing.professionalsDetail.bookingPanel.requestTitleLine1', '¿Cuándo te gustaría coordinar')}<br />{t('landing.professionalsDetail.bookingPanel.requestTitleLine2', 'tu consulta?')}</>
                            : t('landing.professionalsDetail.bookingPanel.availabilityTitle', 'Disponibilidad')}
                    </h3>
                    {bookingMode === 'request' && (
                        <p className={styles['booking__request-intro']}>
                            {props.pricingMode === 'multi'
                                ? t('landing.professionalsDetail.bookingPanel.requestSubtitleMulti', 'Elegí un tipo de consulta, un día y una franja horaria. La hora exacta queda sujeta a confirmación.')
                                : t('landing.professionalsDetail.bookingPanel.requestSubtitle', 'Elegí un día y una franja horaria. La hora exacta queda sujeta a confirmación.')
                            }
                        </p>
                    )}
                </div>

                {!viewsConfirmDate && bookingMode !== 'request' && (
                    <div className={styles['legend']}>
                        <span className={styles['lg']}><i className={styles['ring']}></i>{t('landing.professionalsDetail.bookingPanel.legend.availableDay')}</span>
                        <span className={styles['lg']}><i className={styles['dot-green']}></i>{t('landing.professionalsDetail.bookingPanel.legend.ReservedDay', 'Reservado')}</span>
                    </div>
                )}

                {props.pricingMode === 'multi' && (
                    <div className={styles['booking__type-picker']}>
                        <div className={styles['booking__mini-label']}>{t('landing.professionalsDetail.bookingPanel.consultTypeLabel', 'Tipo de consulta')}</div>
                        <div className={styles['booking__secondary-group']}>
                            <button
                                className={`${styles['booking__secondary']} ${sessionType === 'initial' ? styles['booking__secondary--active'] : ''}`}
                                onClick={() => setSessionType('initial')}
                            >
                                {t('landing.professionalsDetail.bookingPanel.initialConsult', 'Consulta inicial')}
                            </button>
                            <button
                                className={`${styles['booking__secondary']} ${sessionType === 'followup' ? styles['booking__secondary--active'] : ''}`}
                                onClick={() => setSessionType('followup')}
                            >
                                {t('landing.professionalsDetail.bookingPanel.followupConsult', 'Seguimiento')}
                            </button>
                        </div>
                        <p className={styles['booking__type-helper']}>
                            {sessionType === 'initial'
                                ? t('landing.professionalsDetail.bookingPanel.initialHelper', 'Primera entrevista, evaluación o abordaje inicial.')
                                : t('landing.professionalsDetail.bookingPanel.followupHelper', 'Para encuentros posteriores a la consulta inicial.')}
                        </p>
                    </div>
                )}

                <Tooltip
                    open={dayTooltipOpen}
                    title={t('landing.professionalsDetail.bookingPanel.tooltipSelectDay')}
                    {...ERROR_TOOLTIP_PROPS}
                >
                    <div onClick={() => setDayTooltipOpen(false)} className={styles['calendar']} data-calendar>
                        <div className={styles['calendar-top']}>
                            <div className={styles['month']}>
                                {currentMonth.locale(currentLocale).format('MMMM YYYY').replace(/^\w/, (c: string) => c.toLowerCase())}
                            </div>
                            <div className={styles['navs']}>
                                <button
                                    type="button"
                                    className={styles['nav-btn']}
                                    onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                                >
                                    ‹
                                </button>
                                <button
                                    type="button"
                                    className={styles['nav-btn']}
                                    onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                                >
                                    ›
                                </button>
                            </div>
                        </div>
                        <div className={styles['calendar-frame']}>
                            <div className={styles['dow']}>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.mo', 'LU')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.tu', 'MA')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.we', 'MI')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.th', 'JU')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.fr', 'VI')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.sa', 'SÁ')}</span>
                                <span>{t('landing.professionalsDetail.bookingPanel.days.su', 'DO')}</span>
                            </div>
                            <div className={styles['days']}>
                                {renderCalendarGrid()}
                            </div>
                        </div>
                    </div>
                </Tooltip>

                <div className={styles['booking__field-wrapper']}>
                    <Tooltip
                        open={timeTooltipOpen || selectDayFirstTooltipOpen}
                        title={selectDayFirstTooltipOpen ? t('landing.professionalsDetail.bookingPanel.tooltipSelectDayFirst') : t('landing.professionalsDetail.bookingPanel.tooltipSelectTime')}
                        placement="top"
                        arrow
                        {...ERROR_TOOLTIP_PROPS}
                    >
                        <div
                            className={styles['booking__field-inner']}
                            onClick={() => !displayDate && setSelectDayFirstTooltipOpen(true)}
                        >
                            <div className={styles['period-picker']}>
                                <button
                                    className={styles['period-trigger']}
                                    type="button"
                                    aria-expanded={periodMenuOpen}
                                    onClick={() => setPeriodMenuOpen(!periodMenuOpen)}
                                    disabled={!displayDate}
                                    style={{ borderColor: timeTooltipOpen ? '#d32f2f' : undefined, animation: timeTooltipOpen ? `${styles.shake} 0.5s` : 'none' }}
                                >
                                    <span data-period-label>
                                        {bookingMode === 'request'
                                            ? (requestPeriod || t('landing.professionalsDetail.bookingPanel.selectPeriodPlaceholder', 'Elegí un horario'))
                                            : (displayTime ? dayjs(displayTime, 'HH:mm').format('h:mm A') : t('landing.professionalsDetail.bookingPanel.selectTimePlaceholder', 'Elegí un horario disponible'))
                                        }
                                    </span>
                                    {bookingMode === 'direct' ? (
                                        <span className={styles['clock-icon']}>
                                            <svg className={styles['booking__field-icon']} viewBox="0 0 24 24" width="16" height="16">
                                                <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5"></circle>
                                                <path d="M12 8v5l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                            </svg>
                                        </span>
                                    ) : (
                                        <span>⌄</span>
                                    )}
                                </button>
                                <div className={`${styles['period-menu']} ${periodMenuOpen ? styles['is-open'] : ''}`}>
                                    {bookingMode === 'request' ? (
                                        <>
                                            <button
                                                className={`${styles['period-option']} ${requestPeriod === 'Mañana' ? styles['is-active'] : ''}`}
                                                onClick={() => { setRequestPeriod('Mañana'); setPeriodMenuOpen(false); setTimeTooltipOpen(false); }}
                                            >
                                                {t('landing.professionalsDetail.bookingPanel.morning', 'Mañana')}
                                            </button>
                                            <button
                                                className={`${styles['period-option']} ${requestPeriod === 'Tarde' ? styles['is-active'] : ''}`}
                                                onClick={() => { setRequestPeriod('Tarde'); setPeriodMenuOpen(false); setTimeTooltipOpen(false); }}
                                            >
                                                {t('landing.professionalsDetail.bookingPanel.afternoon', 'Tarde')}
                                            </button>
                                        </>
                                    ) : (
                                        displayDate && timeOptions.length > 0 ? (
                                            timeOptions.map((opt: any) => (
                                                <button
                                                    key={opt.value}
                                                    type="button"
                                                    className={`${styles['period-option']} ${displayTime === opt.value ? styles['is-active'] : ''}`}
                                                    onClick={() => { handleTimeUpdate(opt.value); setPeriodMenuOpen(false); setTimeTooltipOpen(false); }}
                                                    disabled={opt.disabled}
                                                >
                                                    {dayjs(opt.value, 'HH:mm').format('h:mm A')}
                                                    {opt.disabled ? ` (${t('landing.professionalsDetail.bookingPanel.Reserved', 'Reservado')})` : ''}
                                                </button>
                                            ))
                                        ) : (
                                            <button type="button" className={styles['period-option']} disabled>
                                                {t('landing.professionalsDetail.bookingPanel.selectTimePlaceholder')}
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </Tooltip>
                </div>

                {modality && (
                    <>

                        <Tooltip
                            open={modalityTooltipOpen}
                            title={t('landing.professionalsDetail.bookingPanel.tooltipSelectModality', 'Selecciona una modalidad')}
                            {...ERROR_TOOLTIP_PROPS}
                        >
                            <div
                                className={styles['booking__split-card']}
                                style={{ gridTemplateColumns: modality === 'Presencial/Virtual' ? '1fr 1fr' : '1fr' }}
                                onClick={() => setModalityTooltipOpen(false)}
                            >
                                {(modality === 'Presencial/Virtual' || modality === 'Virtual') && (
                                    <button
                                        className={bookingModality === 'Virtual' ? styles['is-active'] : ''}
                                        onClick={() => modality === 'Presencial/Virtual' && setBookingModality('Virtual')}
                                        style={{ cursor: modality === 'Presencial/Virtual' ? 'pointer' : 'default' }}
                                    >
                                        <img src={virtualIcon} alt="Virtual" />
                                        <span>{t('landing.professionalsDetail.bookingPanel.virtual', 'Virtual')}</span>
                                    </button>
                                )}
                                {(modality === 'Presencial/Virtual' || modality === 'Presencial') && (
                                    <button
                                        className={bookingModality === 'Presencial' ? styles['is-active'] : ''}
                                        onClick={() => modality === 'Presencial/Virtual' && setBookingModality('Presencial')}
                                        style={{
                                            cursor: modality === 'Presencial/Virtual' ? 'pointer' : 'default',
                                            borderLeft: modality === 'Presencial/Virtual' ? '' : 'none'
                                        }}
                                    >
                                        <img src={presencialIcon} alt="Presencial" />
                                        <span>{t('landing.professionalsDetail.bookingPanel.presential', 'Presencial')}</span>
                                    </button>
                                )}
                            </div>
                        </Tooltip>
                    </>
                )}





                <div className={styles['booking__price-row']}>
                    <div className={styles['booking__price-copy']}>
                        <strong className={styles['booking__price-summary']}>
                            {props.pricingMode === 'multi'
                                ? (sessionType === 'initial' ? t('landing.professionalsDetail.bookingPanel.initialConsult', 'Consulta inicial') : t('landing.professionalsDetail.bookingPanel.followupConsult', 'Seguimiento'))
                                : t('landing.professionalsDetail.bookingPanel.singleConsult', 'Consulta')}
                        </strong>
                        {displayDuration && (
                            <span className={styles['booking__price-duration']}>
                                {displayDuration} min
                            </span>
                        )}
                    </div>
                    <div className={styles['booking__price']}>
                        <span className={styles['booking__price-amount']}>
                            {`${props.currency === 'USD' ? 'US$' : '$U'} ${Number(displayPrice ?? 0).toLocaleString('es-UY')}`}
                        </span>
                    </div>
                </div>

                {!viewsConfirmDate && (
                    <>
                        {bookingMode === 'request' && (dayTooltipOpen || timeTooltipOpen || modalityTooltipOpen) && (
                            <div className={styles['booking__validation-summary']} role="alert">
                                {t('landing.professionalsDetail.bookingPanel.validationSummary', 'Para enviar la solicitud, elegí un día, una franja horaria y una modalidad.')}
                            </div>
                        )}
                        <button
                            onClick={bookingMode === 'request' ? handleRequestSchedule : handleShowBookingModal}
                            disabled={loading || isSubmittingRequest || isPassivePreview}
                            className={styles['booking__cta']}
                        >
                            {bookingMode === 'request'
                                ? t('landing.professionalsDetail.bookingPanel.btnRequestNow', 'Solicitar horario')
                                : t('landing.professionalsDetail.bookingPanel.btnReserveNow', 'Reservar consulta')}
                        </button>
                        {messagingEnabled && (
                            <button
                                type="button"
                                onClick={handleOpenChat}
                                className={styles['booking__secondary-cta']}
                                disabled={isPassivePreview}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M4 5.5h16v11H8l-4 3v-14Z"></path>
                                </svg>
                                <span>{t('landing.professionalsDetail.bookingPanel.sendMessage', 'Enviar mensaje')}</span>
                            </button>
                        )}
                    </>
                )}
            </aside>
            {isPassivePreview && (
                <>
                    <p className={styles['passive-compact-note']}>
                        {t('appAdmin.BookingPanel.passiveRef', 'Referencia visual. La edición se realiza desde')} <strong>{t('appAdmin.BookingPanel.availabilityNav', 'Disponibilidad')}</strong> {t('appAdmin.BookingPanel.inSidebar', 'en el menú lateral.')}
                    </p>
                </>
            )}
        </div>
    );
};